import { compareByNumericProp, compareByProps } from '../../utils/comparators';
import getGlossary from './glossary';

// categories (the lower case are tag values)
const POINT_CATEGORIES = {
  alarm: 'Alarm',
  cmd: 'Command',
  sensor: 'Sensor',
  sp: 'Setpoint',
  limit: 'Parameter',
  virtual: 'Virtual',
};

export const getPointTypeLink = (pointType, base) => {
  const category = pointType.tags.find((c) => POINT_CATEGORIES[c] != null);
  return `${base}/points/${category}/${encodeURIComponent(pointType.name)}`;
};

const buildPointCategories = (pointTypesById, tagCategories, measurementsById) => {
  const makeCategory = (cat) => ({
    type: 'multiselect',
    param: 'tag',
    name: cat,
    children:
      tagCategories[cat]?.map((tag) => ({
        link: tag,
        name: tag,
      })) ?? [],
  });

  const measurements = Object.values(measurementsById);
  const measureChildren = measurements.map((m) => ({
    link: `${m.id}`,
    name: m.name,
  }));
  measureChildren.sort(compareByProps(['name']));

  const categorySections = [
    { ...makeCategory('Point Class'), closeAfterFirst: true },
    {
      type: 'multiselect',
      param: 'measurement',
      name: 'Quantity',
      children: measureChildren,
      closeAfterFirst: true,
    },
    makeCategory('Quantity Modifier'),
    makeCategory('Direction'),
    makeCategory('Medium Property'),
  ];

  return categorySections;
};

const buildEquipmentCategories = (equipTypes) => {
  const namespaces = Object.values(equipTypes || {}).reduce((accumulator, currentValue) => {
    const namespace = currentValue.tagName.split('/')[0];
    // eslint-disable-next-line no-prototype-builtins
    if (accumulator.hasOwnProperty(namespace)) {
      accumulator[namespace].push(currentValue);
    } else {
      accumulator[namespace] = [currentValue];
    }
    return accumulator;
  }, {});

  const equipCategories = Object.entries(namespaces).map((entry) => {
    const namespace = entry[0];
    const values = entry[1];
    return {
      link: namespace,
      name: namespace,
      children: values.map(({ id, tagName, nameLong }) => {
        const generalType = tagName.split('/')[1];
        return {
          id,
          equipId: id,
          link: nameLong,
          name: generalType,
          tagName,
        };
      }),
    };
  });
  return equipCategories;
};

const buildMeasurements = (measurements) => {
  const measurementModels = Object.values(measurements || {}).map(({ id, name }) => ({
    name,
    id,
    link: encodeURIComponent(name),
  }));
  measurementModels.sort(compareByNumericProp('name'));
  return measurementModels;
};

const buildLocations = () => [
  { link: 'CORRIDOR', name: 'CORRIDOR' },
  { link: 'FLOOR', name: 'FLOOR' },
  { link: 'ROOM', name: 'ROOM' },
  { link: 'STAIRWELL', name: 'STAIRWELL' },
];

const buildRelationships = () => [
  { link: 'CONTAINS', name: 'CONTAINS' },
  { link: 'FEEDS', name: 'FEEDS' },
];

const buildModeling = () => [
  { link: 'PUBLISHED', name: 'PUBLISHED' },
  { link: 'PARTIALLY PUBLISHED', name: 'PARTIALLY PUBLISHED' },
  { link: 'UNPUBLISHED', name: 'UNPUBLISHED' },
];

const buildGlossary = () =>
  getGlossary().map((element, index) => ({ link: index, name: element.name }));

const buildDataModel = (tags, tagCategories, equipTypes, pointTypes, measurements) => ({
  name: 'Click to explore',
  children: [
    { link: 'equipment', name: 'Equipment', children: buildEquipmentCategories(equipTypes) },
    {
      link: 'points',
      name: 'Points',
      children: buildPointCategories(pointTypes, tagCategories, measurements),
    },
    { link: 'measurements', name: 'Measurements', children: buildMeasurements(measurements) },
    { link: 'location', name: 'Location', children: buildLocations() },
    { link: 'relationships', name: 'Relationships', children: buildRelationships() },
    { link: 'modeling', name: 'Modeling State', children: buildModeling() },
    { link: 'glossary', name: 'Glossary', children: buildGlossary() },
  ],
});

export default buildDataModel;
